import React from 'react';
import {
  EllipsisForTrans,
  useDomainIntent,
} from '@wix/package-picker-shared-eclipse';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import s from './PageTitle.scss';

const VIEWPORT_PADDING = 220;

const probeStyle: React.CSSProperties = {
  position: 'fixed',
  visibility: 'hidden',
};

export function useDomainNamePageTitle(titleKey: string): React.ReactNode {
  const { t, Trans } = EclipsePackagePickerModule.internals.useTranslations();
  const { shouldShowDomainNameInTitle, domainName } = useDomainIntent();

  const probeEl = React.useRef<HTMLSpanElement | null>(null);
  const [maxWidth, setMaxWidth] = React.useState<number | undefined>(undefined);

  const measure = React.useCallback(() => {
    if (!probeEl.current?.textContent) {
      return;
    }
    const probeWidth = probeEl.current.getBoundingClientRect().width;
    setMaxWidth(
      Math.max(window.innerWidth - probeWidth - VIEWPORT_PADDING, 250),
    );
  }, []);

  const probeText = t(titleKey, { domainName: '' }).replace(/<[^>]+>/g, '');

  // Callback ref: fires when the span mounts so the initial measurement is
  // always correct regardless of when shouldShowDomainNameInTitle becomes true.
  const probeRef = React.useCallback(
    (el: HTMLSpanElement | null) => {
      probeEl.current = el;
      if (el) measure();
    },
    [measure],
  );

  React.useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  if (!shouldShowDomainNameInTitle || !domainName) {
    return null;
  }

  return (
    <>
      <span
        ref={probeRef}
        aria-hidden="true"
        className={s.title}
        style={probeStyle}
      >
        {probeText}
      </span>
      <Trans
        i18nKey={titleKey}
        values={{ domainName }}
        components={{
          ellipsis: (
            <EllipsisForTrans
              maxWidth={maxWidth}
              position="bottom"
              as="span"
              componentProps={{}}
            />
          ),
        }}
      />
    </>
  );
}
